// Per-service and per-industry detail content used by the shared DetailPage template.
// Keep this alongside site.json so copy stays in one place while the JSON remains source-of-truth for names/routes.

export type DetailContent = {
  fit: string[];
  included: { title: string; description: string }[];
  outcomes: string[];
};

export const serviceDetails: Record<string, DetailContent> = {
  "/services/strategic-bookkeeping": {
    fit: [
      "Books that no longer reflect how the business actually operates",
      "Reconciliations, BAS or IAS running late or inconsistently",
      "An owner who is doing bookkeeping tasks that should sit with a specialist",
    ],
    included: [
      {
        title: "Daily bookkeeping and reconciliations",
        description:
          "Bank, credit card, loan and clearing accounts reconciled to a consistent monthly cadence.",
      },
      {
        title: "BAS and IAS preparation",
        description:
          "Prepared and reviewed through a documented process, with clear timelines and lodgement records.",
      },
      {
        title: "Chart of accounts and file health",
        description: "A structure that matches how the business runs, not a generic template.",
      },
      {
        title: "Strategic oversight from Jamee",
        description: "CPA-led review on new engagements and ongoing checkpoints on the file.",
      },
    ],
    outcomes: [
      "Accurate books you can rely on when you need them",
      "Compliance obligations met on a consistent schedule",
      "Fewer late-night queries from the accountant at year-end",
    ],
  },
  "/services/management-reporting": {
    fit: [
      "Owners who want to understand what the numbers mean, not just see them",
      "Businesses making decisions off intuition rather than reports",
      "Boards or advisors asking for consistent monthly information",
    ],
    included: [
      {
        title: "Monthly profit and loss and balance sheet",
        description: "Delivered on a predictable date, with variances explained in plain language.",
      },
      {
        title: "Cash flow view",
        description: "Simple visibility of the movements that drive the position each month.",
      },
      {
        title: "Practical commentary",
        description:
          "Short notes on what changed, what to watch, and where questions are warranted.",
      },
      {
        title: "Custom reporting layers",
        description: "Divisional, job or entity views scoped during the review.",
      },
    ],
    outcomes: [
      "A monthly rhythm the owner and advisors can trust",
      "Earlier visibility of margin, cost or cash flow issues",
      "Confidence in the numbers before big decisions",
    ],
  },
  "/services/payroll": {
    fit: [
      "Growing teams and increasing complexity in awards or entitlements",
      "Payroll currently handled in gaps between other work",
      "Businesses needing consistent Single Touch Payroll processes",
    ],
    included: [
      {
        title: "Weekly, fortnightly or monthly pay runs",
        description: "Processed on a documented schedule with review before release.",
      },
      {
        title: "Single Touch Payroll",
        description: "STP reporting, superannuation and PAYG withholding managed end-to-end.",
      },
      {
        title: "Employee onboarding and offboarding",
        description: "Setup, tax file details, super choice and termination pay handled carefully.",
      },
      {
        title: "Leave and entitlements",
        description: "Accruals, balances and adjustments checked as part of each run.",
      },
    ],
    outcomes: [
      "Employees paid accurately and on time, every cycle",
      "Superannuation and PAYG obligations tracked and lodged",
      "One consistent contact and process, not scattered spreadsheets",
    ],
  },
  "/services/accounts-management": {
    fit: [
      "Supplier bills stacking up or paid late",
      "Overdue customer invoices left sitting because no one owns them",
      "An owner personally chasing money in and out of the business",
    ],
    included: [
      {
        title: "Accounts payable",
        description:
          "Bill capture, approval workflow, scheduled payment runs and supplier query support.",
      },
      {
        title: "Accounts receivable",
        description: "Invoicing support, statements and structured follow-up on overdue accounts.",
      },
      {
        title: "Cash movement visibility",
        description: "A clear view of committed outgoings and expected receipts each week.",
      },
    ],
    outcomes: [
      "Fewer surprises and less firefighting around payments",
      "Faster collection on the accounts that have been sitting",
      "A repeatable process the business can rely on",
    ],
  },
  "/services/xero": {
    fit: [
      "A Xero file that has drifted from how the business now operates",
      "New businesses needing a clean, sensible setup from the start",
      "Teams considering a migration from MYOB, QuickBooks or spreadsheets",
    ],
    included: [
      {
        title: "Xero setup and migration",
        description: "New file build or a structured migration with data integrity checks.",
      },
      {
        title: "File health check",
        description:
          "Chart of accounts, tracking, bank feeds, GST codes and reconciliations reviewed and reset.",
      },
      {
        title: "App and workflow selection",
        description:
          "Practical recommendations on approvals, receipts, payroll and payments stack.",
      },
      {
        title: "Training and handover",
        description:
          "Short, focused sessions with the owner or internal team on what to use day-to-day.",
      },
    ],
    outcomes: [
      "A Xero file that supports decisions rather than getting in the way",
      "Cleaner data feeding into reporting and the accountant",
      "Confidence that the setup is right for the next stage of growth",
    ],
  },
};

export const industryDetails: Record<string, DetailContent> = {
  "/industries/professional-services": {
    fit: [
      "Firms where people cost is the largest line and utilisation matters",
      "Owners who need consistent cash collection to fund payroll",
      "Partners wanting clearer monthly reporting without an internal finance hire",
    ],
    included: [
      {
        title: "Payroll and contractor payments",
        description: "Managed carefully so the team is paid accurately and on schedule.",
      },
      {
        title: "Reporting by service line or division",
        description: "Set up during the review so profitability is visible where it matters.",
      },
      {
        title: "Accounts receivable discipline",
        description: "Structured follow-up so cash collection does not sit on the owner.",
      },
    ],
    outcomes: [
      "A stable monthly rhythm despite lumpy fee timing",
      "Clearer margin visibility across service lines",
      "Time back for partners to focus on client work",
    ],
  },
  "/industries/construction-trades": {
    fit: [
      "Businesses with a mix of employees and subcontractors",
      "Variable cash flow tied to project milestones and progress claims",
      "Owners juggling supplier accounts, jobs and payroll each week",
    ],
    included: [
      {
        title: "Payroll and contractor management",
        description: "Awards, allowances, super and STP handled through a consistent process.",
      },
      {
        title: "Supplier and subcontractor accounts",
        description: "Approval workflows and scheduled payment runs to keep jobs moving.",
      },
      {
        title: "Reporting the owner can act on",
        description: "A monthly view of profit, cash and outstanding accounts in plain language.",
      },
    ],
    outcomes: [
      "Fewer late payments and less chasing at end of month",
      "Confidence that BAS, super and payroll are on track",
      "Owners spending more time on the business, less on admin",
    ],
  },
  "/industries/health-dental": {
    fit: [
      "Practices with growing teams and increasing complexity",
      "Owners wanting dependable finance operations across multiple locations",
      "Practice managers needing accurate figures without doing bookkeeping themselves",
    ],
    included: [
      {
        title: "Payroll and superannuation",
        description: "Managed for clinical and admin staff through documented processes.",
      },
      {
        title: "Practice reporting",
        description: "Monthly reports the owner and practice manager can actually use.",
      },
      {
        title: "Compliance rhythm",
        description: "BAS, IAS and reconciliations lodged on time, every time.",
      },
    ],
    outcomes: [
      "Reliable finance operations while the team focuses on patients",
      "Clarity on what each location or service is contributing",
      "Fewer surprises at year-end or from the accountant",
    ],
  },
  "/industries/property": {
    fit: [
      "Property services businesses with recurring suppliers and trust considerations",
      "Owners managing multiple entities or portfolios",
      "Teams needing dependable payroll and supplier workflows",
    ],
    included: [
      {
        title: "Multi-entity bookkeeping",
        description: "Consistent processes and reporting across related businesses.",
      },
      {
        title: "Supplier and payroll rhythm",
        description: "Payments, awards and super handled through documented routines.",
      },
      {
        title: "Reporting owners can rely on",
        description: "Monthly management reports without needing to chase them.",
      },
    ],
    outcomes: [
      "A single, coordinated view of the finance function",
      "Less time in the file, more time on the business",
      "Reporting that supports the next investment decision",
    ],
  },
  "/industries/automotive": {
    fit: [
      "Workshops, distributors and automotive service businesses",
      "Owners balancing inventory, jobs and payroll each week",
      "Businesses that have outgrown the bookkeeper who set them up",
    ],
    included: [
      {
        title: "Industry-aware bookkeeping",
        description:
          "A Xero setup and chart of accounts that reflects how the workshop actually runs.",
      },
      {
        title: "Payroll and supplier accounts",
        description: "Structured processes for pay runs, super and supplier payments.",
      },
      {
        title: "Monthly reporting",
        description: "Simple, useful reports without jargon or noise.",
      },
    ],
    outcomes: [
      "Cleaner books and stronger visibility of margin",
      "Fewer overdue supplier and compliance issues",
      "An owner able to plan, not just react",
    ],
  },
  "/industries/service-businesses": {
    fit: [
      "Established service businesses that have outgrown basic bookkeeping",
      "Owners wanting an outsourced finance function without hiring a CFO",
      "Teams needing a coordinated view of bookkeeping, payroll and reporting",
    ],
    included: [
      {
        title: "Coordinated finance operations",
        description: "Bookkeeping, payroll, accounts management and reporting under one scope.",
      },
      {
        title: "Predictable monthly rhythm",
        description: "Delivery dates, responsibilities and reporting all defined during setup.",
      },
      {
        title: "Strategic oversight",
        description: "Jamee stays involved on the file so the direction remains sound.",
      },
    ],
    outcomes: [
      "One team handling the finance function end-to-end",
      "Clarity on performance and where to focus next",
      "Room for the owner to work on the business again",
    ],
  },
};
